<template>
    <app-layout
        :silent-login-enabled="config.silentLoginEnabled"
        :error-boundary-options="errorBoundaryOptions"
        :v-app-options="{ class: 'webshop-app' }"
        hide-footer
    >
        <v-main v-if="mainStore.ready" class="d-flex flex-column align-center justify-center w-100">
            <v-container fluid max-width="600" class="position-relative py-10 h-100">
                <router-view />
            </v-container>
        </v-main>
    </app-layout>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { getCurrentInstance, nextTick, provide, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useMainStore from '@/features/common/stores/mainStore';
import { AppLayout } from '@geta/kolumbus-frontend/components';
import { AppProvideKey } from './injectionKeys';
import { createGtm } from 'vue-gtm';
import { routeNames } from './router';
import { i18nRoute } from './i18n';
import { useAppStore } from '@geta/kolumbus-frontend/features/common';

const errorBoundaryOptions = {
    errorsToDisplay: ['ProfileError', 'TravelCardError']
};

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const mainStore = useMainStore();
const { language } = storeToRefs(appStore);
const { config } = storeToRefs(mainStore);

const gmapsApiKey = ref('');

provide(AppProvideKey, { gmapsApiKey });

watch(language, async (value, oldValue) => {
    if (value !== oldValue) {
        try {
            await router.replace({ params: { locale: value }, query: route.query });
        } catch (e) {
            mainStore.registerError(e);
            router.push(i18nRoute({ name: routeNames.home }));
        }
    }
});

const instance = getCurrentInstance();

watch(
    () => appStore.ready,
    () => {
        if (instance && config.value?.googleTagManagerConfig?.id) {
            instance.appContext.app.use(createGtm({ id: config.value.googleTagManagerConfig.id }));
        }

        if (mainStore.config.googleMapsConfig.apiKey) {
            gmapsApiKey.value = mainStore.config.googleMapsConfig.apiKey;
        }

        nextTick(() => (mainStore.ready = true));
    },
    {
        once: true
    }
);
</script>

<style lang="scss">
.webshop-app {
    &__header {
        z-index: 1000;
    }

    &__drawer {
        z-index: 900;
    }
}

.v-main {
    z-index: 1;

    .v-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style>
